/* Resilience for every live proxima-audiences call: timeouts, cancellation, retries, token refresh, and error normalization. */
import { AxiosError, AxiosHeaders, type AxiosResponse, type RawAxiosRequestConfig } from 'axios';
import { logError } from '../lib/logError';
import type {
  ApiResult,
  CustomAudiencesApiContext,
  CustomAudiencesApiError,
  ValidationError
} from '../types';

/* A dev build falls back to a local proxima-audiences; every other build must set the URL */
const DEV_AUDIENCES_API_URL = 'http://localhost:9083';

export const MISSING_API_URL_MESSAGE =
  'Custom Audiences API is not configured: set VITE_AUDIENCES_API_URL.';

/* Preview is one model call plus a repair call, which can take most of a minute */
const WRITE_TIMEOUT_MS = 90_000;
/* A delete waits on Meta once for each audience it removes */
export const DELETE_TIMEOUT_MS = 180_000;
/* A GET that takes longer counts as a timeout, and is retried like one */
export const READ_TIMEOUT_MS = 20_000;

/* A GET is retried at most this many times after its first attempt */
export const MAX_READ_RETRIES = 2;
/* The first retry waits about this long, and each later one doubles it, with jitter */
export const RETRY_BASE_DELAY_MS = 500;
/* A `Retry-After` longer than this ends the retries instead of waiting */
export const RETRY_MAX_DELAY_MS = 10_000;

/* Gateway and throttling statuses, where the request can succeed on a later attempt */
const RETRYABLE_STATUSES = [429, 502, 503, 504];
/* Axios codes for a request that got no response */
const RETRYABLE_CODES = ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'];

const TIMEOUT_MS_BY_METHOD = {
  GET: READ_TIMEOUT_MS,
  POST: WRITE_TIMEOUT_MS,
  PATCH: WRITE_TIMEOUT_MS,
  DELETE: DELETE_TIMEOUT_MS
} as const;

const TIMEOUT_ERROR: CustomAudiencesApiError = {
  status: null,
  message: 'The request timed out',
  code: 'ETIMEDOUT'
};
const CANCELED_ERROR: CustomAudiencesApiError = {
  status: null,
  message: 'The request was canceled',
  code: 'ERR_CANCELED'
};

/**
 * One SDK call, sent with the attempt's options.
 *
 * The options carry the attempt's `signal` and its `Authorization` header, the only one the request sends.
 */
export type SdkCall<T> = (options: RawAxiosRequestConfig) => Promise<AxiosResponse<T>>;

export type SendOptions = {
  /** Retry on gateway and connection failures as a GET is; only for a write that is idempotent. */
  retry?: boolean;
};

/**
 * Sends one proxima-audiences SDK call and returns its data or a normalized error; it never throws.
 *
 * - Each attempt aborts after `READ_TIMEOUT_MS` for a GET, `DELETE_TIMEOUT_MS` for a DELETE, or `WRITE_TIMEOUT_MS` otherwise, and when `ctx.signal` aborts.
 * - A 401 is retried once with a refreshed token, since a cached token can expire before its stated lifetime; a dev token is never refreshed.
 * - A GET that got no response, a 429, or a 502, 503, or 504 is retried up to `MAX_READ_RETRIES` times, as `retryDelayMs` spaces them.
 * - POST, PATCH, and DELETE are not retried on those unless sent with `retry: true`.
 *   Create, deliver, and preview are not idempotent, and a delete's 502 is a Meta refusal that a retry would repeat.
 */
export const sendCustomAudiencesRequest = async <T>(
  ctx: CustomAudiencesApiContext,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  call: SdkCall<T>,
  options: SendOptions = {}
): Promise<ApiResult<T>> => {
  if (!ctx.brandId) return { error: { status: null, message: 'Missing brandId' } };
  if (!customAudiencesBaseUrl()) {
    logError('config.missingApiUrl', MISSING_API_URL_MESSAGE);
    return { error: { status: null, message: MISSING_API_URL_MESSAGE } };
  }
  const read = method === 'GET';
  const retry = options.retry ?? read;
  const timeoutMs = TIMEOUT_MS_BY_METHOD[method];

  let tokenRefreshed = false;
  let refreshToken = false;
  let retries = 0;
  for (;;) {
    // biome-ignore lint/performance/noAwaitInLoops: each attempt waits on the previous one's outcome
    const result = await attempt<T>(ctx, call, timeoutMs, refreshToken);
    refreshToken = false;
    if (!result.error) return { data: result.data };
    const { error } = result;
    if (error.status === 401 && !tokenRefreshed && !devToken()) {
      tokenRefreshed = true;
      refreshToken = true;
      continue;
    }
    if (!retry || retries >= MAX_READ_RETRIES || !isRetryable(error)) return { error };
    const delay = retryDelayMs(retries, result.retryAfter);
    if (delay == null) return { error };
    retries += 1;
    if (!(await sleep(delay, ctx.signal))) return { error: CANCELED_ERROR };
  }
};

/** The proxima-audiences base URL, or null when a non-dev build has none set. */
export const customAudiencesBaseUrl = (): string | null =>
  import.meta.env.VITE_AUDIENCES_API_URL || (import.meta.env.DEV ? DEV_AUDIENCES_API_URL : null);

/**
 * Normalizes an axios failure.
 *
 * - A `ValidationError[]` body keeps its entries and joins their messages.
 * - A bare JSON string body becomes the message.
 * - A `{ message }` body keeps its message.
 * - No response keeps the axios message and code.
 */
export const toCustomAudiencesApiError = (error: unknown): CustomAudiencesApiError => {
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? null;
    const body: unknown = error.response?.data;
    if (isValidationErrors(body))
      return {
        status,
        message: body.map(item => item.message).join('; '),
        validationErrors: body
      };
    if (typeof body === 'string' && body.trim()) return { status, message: body };
    if (body != null && typeof body === 'object' && 'message' in body)
      return { status, message: String((body as { message: unknown }).message) };
    return {
      status,
      message: status ? `Request failed (${status})` : error.message,
      ...(status == null && error.code ? { code: error.code } : {})
    };
  }
  return { status: null, message: error instanceof Error ? error.message : 'Request failed' };
};

type AttemptResult<T> =
  | { data: T; error?: undefined }
  | { data?: undefined; error: CustomAudiencesApiError; retryAfter: number | null };

/* One attempt, aborted after `timeoutMs` or when the caller's signal aborts */
const attempt = async <T>(
  ctx: CustomAudiencesApiContext,
  call: SdkCall<T>,
  timeoutMs: number,
  refreshToken: boolean
): Promise<AttemptResult<T>> => {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const onCallerAbort = () => controller.abort();
  ctx.signal?.addEventListener('abort', onCallerAbort, { once: true });
  try {
    const token = devToken() ?? (await ctx.getToken(refreshToken ? { refresh: true } : undefined));
    const response = await call({
      signal: controller.signal,
      headers: { Authorization: `Bearer ${token}` }
    });
    return { data: response.data };
  } catch (error) {
    if (ctx.signal?.aborted) return { error: CANCELED_ERROR, retryAfter: null };
    if (timedOut) return { error: TIMEOUT_ERROR, retryAfter: null };
    return { error: toCustomAudiencesApiError(error), retryAfter: retryAfterMs(error) };
  } finally {
    clearTimeout(timer);
    ctx.signal?.removeEventListener('abort', onCallerAbort);
  }
};

/* A dev build with VITE_AUDIENCES_DEV_TOKEN sends that bearer to proxima-audiences in place of the signed-in user's token, e.g. a develop service token for a local service. */
const devToken = (): string | null =>
  (import.meta.env.DEV && import.meta.env.VITE_AUDIENCES_DEV_TOKEN) || null;

/* `Retry-After` is seconds or an HTTP date */
const retryAfterMs = (error: unknown): number | null => {
  if (!(error instanceof AxiosError) || !error.response) return null;
  const header = AxiosHeaders.from(error.response.headers as AxiosHeaders).get('retry-after');
  if (typeof header !== 'string' || !header.trim()) return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(header);
  return Number.isNaN(date) ? null : Math.max(0, date - Date.now());
};

const isRetryable = (error: CustomAudiencesApiError): boolean =>
  error.status == null
    ? error.code != null && RETRYABLE_CODES.includes(error.code)
    : RETRYABLE_STATUSES.includes(error.status);

/**
 * How long to wait before retry number `retry` (zero-based), or null to stop retrying.
 *
 * - Without `Retry-After`, the wait is `RETRY_BASE_DELAY_MS` doubled per retry, with equal jitter.
 * - With it, the wait is never shorter than the header asks, and a header past `RETRY_MAX_DELAY_MS` stops the retries.
 */
const retryDelayMs = (retry: number, retryAfter: number | null): number | null => {
  const backoff = RETRY_BASE_DELAY_MS * 2 ** retry;
  const jittered = backoff / 2 + Math.random() * (backoff / 2);
  if (retryAfter == null) return jittered;
  return retryAfter > RETRY_MAX_DELAY_MS ? null : Math.max(retryAfter, jittered);
};

/* Resolves true after `ms`, or false as soon as `signal` aborts */
const sleep = (ms: number, signal: AbortSignal | undefined): Promise<boolean> =>
  new Promise(resolve => {
    if (signal?.aborted) {
      resolve(false);
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve(false);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve(true);
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });

const isValidationErrors = (body: unknown): body is ValidationError[] =>
  Array.isArray(body) &&
  body.every(
    item => item != null && typeof item === 'object' && 'path' in item && 'message' in item
  );
